import { format, formatDistanceToNowStrict } from 'date-fns'
import type {
  UserActivity,
  UserAdminDetailsResponse,
  UserAdvancedSearchAdminInfo,
  UserTimelineEvent,
} from 'api/generated'
import { it } from 'date-fns/locale'
import { CLIENT_PLATFORM_LABELS, SOURCE_LABELS } from 'helpers/user-filters'
import {
  Ban,
  CreditCard,
  Dumbbell,
  Flag,
  Gamepad2,
  IdCard,
  LogIn,
  type LucideIcon,
  MessageCircle,
  Send,
  Newspaper,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Swords,
  Ticket,
  Trophy,
  UserPlus,
  Users,
  UserX,
  Watch,
} from 'lucide-react'

export const formatDay = (value?: string | null) =>
  value ? format(new Date(value), 'dd/MM/yyyy') : '—'

export const formatDateTime = (value?: string | null) =>
  value ? format(new Date(value), 'dd/MM/yyyy HH:mm') : '—'

/** "3 giorni fa"; empty without a date. */
export const timeAgo = (value?: string | null) =>
  value
    ? formatDistanceToNowStrict(new Date(value), {
        locale: it,
        addSuffix: true,
      })
    : ''

export const yesNo = (value?: boolean | null) =>
  value === null || value === undefined ? '—' : value ? 'Sì' : 'No'

export const LOGIN_METHOD_NAMES: Record<string, string> = {
  password: 'Email e password',
  apple: 'Apple',
  google: 'Google',
  facebook: 'Facebook',
  facebook_limited: 'Facebook (limited)',
  email_confirmation: 'Link di conferma',
  backoffice: 'Backoffice',
}

export interface ClientSummary {
  /** App or web, null when nothing tells it. */
  kind: 'app' | 'web' | null
  /** e.g. "App iOS 3.6.4", "Web", "Non tracciato". */
  label: string
  /** Where the answer comes from, for the tooltip. */
  source: string
}

/**
 * App or web for a user, from the most precise signal available: the last
 * client seen (platform header, refreshed on every request), else the last
 * login source, else the push tokens (only the app registers them).
 */
export const clientSummary = (
  admin: UserAdvancedSearchAdminInfo | null | undefined
): ClientSummary => {
  if (!admin) return { kind: null, label: '—', source: '' }
  if (admin.client_platform) {
    const kind = admin.client_platform === 'web' ? 'web' : 'app'
    return {
      kind,
      label: [
        CLIENT_PLATFORM_LABELS[admin.client_platform],
        admin.client_version,
      ]
        .filter(Boolean)
        .join(' '),
      source: 'Ultimo client visto',
    }
  }
  if (admin.last_sign_in_source)
    return {
      kind: admin.last_sign_in_source,
      label: admin.last_sign_in_source === 'app' ? 'App' : 'Web',
      source: 'Ultimo login (versione non ancora inviata)',
    }
  const app = admin.platforms.filter((platform) => platform !== 'web')
  if (app.length)
    return {
      kind: 'app',
      label: `App (${app.join(', ')})`,
      source: 'Token push registrati',
    }
  return { kind: null, label: 'Non tracciato', source: 'Nessun dato client' }
}

export type ActivityKey = keyof UserActivity

/** The activity metrics, grouped as the dialog shows them. */
export const ACTIVITY_GROUPS: {
  title: string
  metrics: { key: ActivityKey; label: string }[]
}[] = [
  {
    title: 'Gioco',
    metrics: [
      { key: 'user_matches_played', label: 'Partite giocate' },
      { key: 'match_finder_organised', label: 'Trova partita: create' },
      { key: 'match_finder_joined', label: 'Trova partita: a cui partecipa' },
      { key: 'trainings', label: 'Allenamenti creati' },
      { key: 'training_participations', label: 'Partecipazioni allenamenti' },
      { key: 'diary_activities', label: 'Voci del diario' },
      { key: 'smart_watch_workouts', label: 'Workout smartwatch' },
      { key: 'live_event_games', label: 'Partite live event' },
    ],
  },
  {
    title: 'Social',
    metrics: [
      { key: 'posts', label: 'Post' },
      { key: 'comments', label: 'Commenti' },
      { key: 'likes_given', label: 'Like messi' },
      { key: 'likes_received', label: 'Like ricevuti' },
      { key: 'followers', label: 'Follower' },
      { key: 'following', label: 'Seguiti' },
      { key: 'messages_sent', label: 'Messaggi inviati' },
      { key: 'messages_received', label: 'Messaggi ricevuti' },
    ],
  },
  {
    title: 'Navigazione',
    metrics: [
      { key: 'fit_profile_visits_made', label: 'Profili FIT visitati' },
      {
        key: 'fit_profile_visits_received',
        label: 'Visite al suo profilo FIT',
      },
      { key: 'twe_product_clicks', label: 'Click prodotti TWE' },
      { key: 'offers', label: 'Annunci mercatino' },
      { key: 'notifications', label: 'Notifiche ricevute' },
    ],
  },
  {
    title: 'Moderazione',
    metrics: [
      { key: 'reports_sent', label: 'Segnalazioni fatte' },
      { key: 'reports_received', label: 'Segnalazioni ricevute' },
      { key: 'blocks_given', label: 'Utenti bloccati' },
      { key: 'blocks_received', label: 'Bloccato da' },
    ],
  },
]

/** The metrics of the overview tab. */
export const KEY_METRICS: { key: ActivityKey; label: string }[] = [
  { key: 'user_matches_played', label: 'Partite' },
  { key: 'match_finder_organised', label: 'Trova partita' },
  { key: 'trainings', label: 'Allenamenti' },
  { key: 'messages_sent', label: 'Messaggi' },
  { key: 'posts', label: 'Post' },
  { key: 'followers', label: 'Follower' },
  { key: 'fit_profile_visits_made', label: 'Profili visitati' },
  { key: 'smart_watch_workouts', label: 'Workout' },
]

export const MATCH_STATUS_LABELS: Record<
  keyof UserAdminDetailsResponse['match_finder_statuses'],
  string
> = {
  open: 'Aperte',
  full: 'Complete',
  proposed: 'Proposte',
  played: 'Giocate',
  cancelled: 'Annullate',
}

/** Noticed notifier types, as the backoffice names them. */
export const NOTIFIER_LABELS: Record<string, string> = {
  'CommentNotifier::Notification': 'Commento',
  'CommentToCommentNotifier::Notification': 'Risposta a commento',
  'FollowNotifier::Notification': 'Follower / nuovo post',
  'GeneralNotifier::Notification': 'Generale',
  'MembershipPaymentNotifier::Notification': 'Pagamento membership',
  'MessageNotifier::Notification': 'Messaggio',
  'NewMatchNotifier::Notification': 'Partita FIT',
  'NewTournamentNotifier::Notification': 'Nuovo torneo',
  'RankingNotifier::Notification': 'Classifica',
  'TrainingNotifier::Notification': 'Allenamento',
  'UserMatchNotifier::Notification': 'Partita / Trova partita',
}

type TimelineKind = UserTimelineEvent['kind']

const GOOD = 'text-lime-700 border-lime-300'
const BAD = 'text-red-600 border-red-300'

export const TIMELINE_KINDS: Record<
  TimelineKind,
  { label: string; Icon: LucideIcon; tone?: string }
> = {
  signup: { label: 'Iscrizione', Icon: Sparkles, tone: GOOD },
  login: { label: 'Login (sessione attiva)', Icon: LogIn },
  ban: { label: 'Bannato', Icon: Ban, tone: BAD },
  membership_code: { label: 'Codice membership', Icon: Ticket, tone: GOOD },
  subscription: { label: 'Abbonamento Recharge', Icon: CreditCard, tone: GOOD },
  checkout: { label: 'Checkout Shopify aperto', Icon: ShoppingCart },
  fit_request: { label: 'Richiesta tessera FIT', Icon: IdCard },
  post: { label: 'Post', Icon: Newspaper },
  comment: { label: 'Commento', Icon: MessageCircle },
  message_sent: { label: 'Messaggio inviato', Icon: Send },
  follow: { label: 'Segue', Icon: UserPlus },
  user_match: { label: 'Partita giocata', Icon: Trophy },
  match_finder_created: { label: 'Trova partita: creata', Icon: Swords },
  match_finder_joined: { label: 'Trova partita: partecipa', Icon: Users },
  training: { label: 'Allenamento', Icon: Dumbbell },
  training_participation: {
    label: 'Partecipa a un allenamento',
    Icon: Dumbbell,
  },
  workout: { label: 'Workout smartwatch', Icon: Watch },
  offer: { label: 'Annuncio mercatino', Icon: ShoppingBag },
  report_sent: { label: 'Segnalazione fatta', Icon: Flag },
  report_received: { label: 'Segnalato', Icon: Flag, tone: BAD },
  block: { label: 'Ha bloccato', Icon: UserX },
  live_event_game: { label: 'Partita live event', Icon: Gamepad2 },
}

const FIT_REQUEST_STATUS: Record<string, string> = {
  pending: 'in attesa',
  approved: 'approvata',
  rejected: 'rifiutata',
}

const str = (value: unknown) =>
  value === null || value === undefined || value === ''
    ? undefined
    : String(value)

/** One line about the event, from its detail and data. */
export const timelineDescription = (event: UserTimelineEvent) => {
  const { data, detail } = event
  const client = [
    data.client_platform
      ? CLIENT_PLATFORM_LABELS[String(data.client_platform)]
      : undefined,
    str(data.client_version),
  ]
    .filter(Boolean)
    .join(' ')
  const parts: (string | undefined)[] = (() => {
    switch (event.kind) {
      case 'signup':
      case 'login':
        return [
          data.method ? LOGIN_METHOD_NAMES[String(data.method)] : undefined,
          data.source ? SOURCE_LABELS[String(data.source)] : undefined,
          client,
        ]
      case 'ban':
        return [detail ?? undefined, str(data.by) && `da ${data.by}`]
      case 'membership_code':
        return [
          detail ?? undefined,
          str(data.plan),
          data.paid ? `ordine ${data.shopify_order_id}` : 'manuale / omaggio',
          data.activated ? 'attivato' : 'da attivare',
        ]
      case 'subscription':
        return [detail ?? undefined, str(data.linked_via)]
      case 'checkout':
        return [
          data.used
            ? `concluso, ordine ${data.shopify_order_id}`
            : 'non concluso',
        ]
      case 'fit_request':
        return [
          detail ?? undefined,
          str(data.fit_player_id),
          FIT_REQUEST_STATUS[String(data.status)],
        ]
      case 'user_match':
        return [
          str(data.opponent) && `vs ${data.opponent}`,
          detail ?? undefined,
          data.competitive ? 'ufficiale' : 'amichevole',
          str(data.date) && formatDay(String(data.date)),
        ]
      case 'match_finder_created':
      case 'match_finder_joined':
        return [
          detail ?? undefined,
          str(data.starts_at) && formatDateTime(String(data.starts_at)),
          data.doubles ? 'doppio' : 'singolo',
          str(data.status),
        ]
      case 'training':
      case 'training_participation':
        return [
          detail ?? undefined,
          str(data.date) && formatDay(String(data.date)),
        ]
      case 'workout':
        return [
          detail ?? undefined,
          str(data.duration) && `${Math.round(Number(data.duration) / 60)} min`,
        ]
      case 'report_sent':
        return [str(data.about), detail ?? undefined, str(data.status)]
      // The text is shown apart, collapsed
      case 'message_sent':
        return [
          str(data.recipient) && `a ${data.recipient}`,
          str(data.as_profile) && `come ${data.as_profile}`,
          data.blocked
            ? 'bloccato'
            : data.deleted
              ? 'cancellato'
              : data.read
                ? 'letto'
                : 'non letto',
        ]
      case 'report_received':
        return [detail ?? undefined, str(data.status)]
      case 'live_event_game':
        return [
          detail ?? undefined,
          str(data.score) && `punteggio ${data.score}`,
          data.invalidated ? 'invalidata' : str(data.status),
        ]
      default:
        return [detail ?? undefined]
    }
  })()
  return parts.filter(Boolean).join(' · ')
}
